// Instellingen (alleen de superadmin), zoals het ontwerp (design/build/proto-instellingen-logic.js): werktijd,
// planningsregels, labels, rollen en rechten, bronnen en meldingen. Wijzigingen blijven een concept tot Opslaan; de
// server controleert elke sleutel en houdt elke wijziging bij in het logboek (Laatste wijzigingen).
// Feestdagen komen uit Clockify; Bronnen toont de stand van Clockify (gekoppeld, laatst bijgewerkt of de fout). Wat
// verder Clockify of de supportmailbox nodig heeft (afwijkende werkweek, supportreservering, effect op de bezetting,
// achterstand verdelen, ontbrekende uren, supportmails) volgt nog.
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { useClockifyLive, type ClockifyStatus } from "../lib/api-clockify";
import { useSupport, type SupportSync } from "../lib/api-support";
import { settingsApi, type NotifyKey, type Settings, type SettingsInfo } from "../lib/api-settings";
import { labelColor } from "../lib/board";
import { useIndex, useMe, usePlanning } from "../lib/hooks";
import { Icon } from "../lib/icons";
import { addDays, ddmm, dow, todayIn, type LabelMeaning } from "../lib/planning";
import { useApplySaved, useSettings } from "../lib/settings";
import { LoadError } from "../shell/Layout";
import { useToast } from "../shell/Toast";

type Tab = "werktijd" | "regels" | "labels" | "rollen" | "bronnen" | "meldingen";
type Key = keyof Settings;
const TABS: [Tab, string][] = [["werktijd", "Werktijd en capaciteit"], ["regels", "Planningsregels"], ["labels", "Labels uit Planner"], ["rollen", "Rollen en rechten"], ["bronnen", "Bronnen"], ["meldingen", "Meldingen"]];
const DAYS = ["ma", "di", "wo", "do", "vr", "za", "zo"];
const CATS: [LabelMeaning, string][] = [["actief", "Actief werk"], ["geparkeerd", "Geparkeerd"], ["afsluiten", "Klaar om af te sluiten"], ["negeren", "Negeren"]];
// grenzen zoals in backend/apps/planning/services/settings.py
const NUM: Partial<Record<Key, [number, number]>> = { weekHours: [1, 60], soonDays: [1, 30], lateDays: [1, 120], horizonWeeks: [4, 16], full: [50, 150], over: [50, 200], room: [0, 100] };
const TAB_OF: Partial<Record<Key, Tab>> = {
  weekHours: "werktijd", workdays: "werktijd", closingDays: "werktijd",
  soonDays: "regels", lateDays: "regels", horizonWeeks: "regels", full: "regels", over: "regels", room: "regels",
};

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
const plural = (n: number, one: string, many: string) => n + " " + (n === 1 ? one : many);

/** "vandaag 10:15", "gisteren 16:40" of "25/09 16:02" */
function when(iso: string): string {
  const at = new Date(iso), day = todayIn("Europe/Brussels", at), today = todayIn();
  const hm = at.toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Brussels" });
  return (day === today ? "vandaag" : day === addDays(today, -1) ? "gisteren" : ddmm(day)) + " " + hm;
}

/** Stand van Clockify in Bronnen: tekst en kleur. */
function clockifyText(s: ClockifyStatus | undefined, failed: boolean): [string, string] {
  if (!s) return failed ? ["Stand kon niet laden", "t-warn"] : ["…", ""];
  if (s.state === "ok") return ["Gekoppeld · laatst bijgewerkt " + (s.lastOk ? when(s.lastOk).replace(/^vandaag /, "") : "–"), "t-ok"];
  if (s.state === "fout") return [(s.errorKind === "toegang" ? "Geen toegang: " : "Fout: ") + s.error, "t-err"];
  if (s.state === "wacht") return ["Gekoppeld · de eerste lezing volgt", "t-warn"];
  return ["Niet gekoppeld", "t-warn"];
}

function mailText(s: SupportSync | undefined, failed: boolean): [string, string] {
  if (!s) return failed ? ["Stand kon niet laden", "t-warn"] : ["…", ""];
  if (s.state === "ok") return ["Gekoppeld (" + s.mailbox + ") · laatst bijgewerkt " + (s.lastOk ? when(s.lastOk).replace(/^vandaag /, "") : "–"), "t-ok"];
  if (s.state === "toegang") return ["Geen toegang tot " + s.mailbox, "t-err"];
  if (s.state === "fout") return ["Fout: " + s.error, "t-err"];
  if (s.state === "nooit") return ["Gekoppeld · de eerste lezing volgt", "t-warn"];
  return ["Niet gekoppeld", "t-warn"];
}

function every(seconds: number): string {
  if (seconds === 60) return "Elke minuut";
  return seconds % 60 === 0 ? "Elke " + seconds / 60 + " minuten" : "Elke " + seconds + " seconden";
}

/** Eén instelling: naam en uitleg links, de keuze rechts. */
function Row({ label, help, error, block, children }: { label: string; help?: ReactNode; error?: string; block?: boolean; children: ReactNode }) {
  return (
    <div className={"p-set" + (block ? " p-set-block" : "")}>
      <div className="p-set-l"><b>{label}</b>{help && <span>{help}</span>}{error && <span className="t-err" role="alert">{error}</span>}</div>
      <div className="p-set-c">{children}</div>
    </div>
  );
}

function Toggle({ label, on, onClick }: { label: string; on: boolean; onClick: () => void }) {
  return <button type="button" role="switch" className="ex-switch" aria-checked={on} aria-label={label} onClick={onClick}><span /></button>;
}

function Radios<T extends string>({ label, value, opts, onPick }: { label: string; value: T; opts: [T, string][]; onPick: (v: T) => void }) {
  return (
    <div className="p-set-radios" role="radiogroup" aria-label={label}>
      {opts.map(([v, text]) => (
        <button key={v} type="button" role="radio" aria-checked={value === v} className={"p-opt1" + (value === v ? " on" : "")} onClick={() => onPick(v)}>
          <span className={"p-radio" + (value === v ? " on" : "")} />{text}
        </button>
      ))}
    </div>
  );
}

export function Instellingen() {
  const { data: me } = useMe();
  const q = useSettings();
  if (!me) return null;
  if (!me.superadmin) {
    return (
      <>
        <div className="p-head"><div><h1>Instellingen</h1><p>Alleen voor de superadmin</p></div></div>
        <div className="ex-notice ex-notice--mist p-notice"><Icon name="info" size={16} /><p>Instellingen beheert de superadmin. Ze gelden voor iedereen; u hoeft hier niets te doen.</p></div>
      </>
    );
  }
  if (!q.data && q.error) return <LoadError what="De instellingen" onRetry={() => q.refetch()} />;
  if (!q.data) return <div className="p-loading">Instellingen laden…</div>;
  return <Editor saved={q.data.settings} info={q.data.info} meId={me.id} />;
}

function Editor({ saved, info, meId }: { saved: Settings; info?: SettingsInfo; meId: string }) {
  const toast = useToast();
  const applySaved = useApplySaved();
  const { data } = usePlanning();
  const idx = useIndex(data);
  const audit = useQuery({ queryKey: ["audit", "settings."], queryFn: () => settingsApi.audit(5, "settings."), refetchInterval: 60e3 });
  const live = useClockifyLive();
  const support = useSupport(null);
  const [tab, setTab] = useState<Tab>("werktijd");
  const [draft, setDraft] = useState<Partial<Settings>>({});
  // getallen zoals getypt: een ongeldige waarde blijft staan met een melding, het concept houdt de laatste geldige
  const [txt, setTxt] = useState<Partial<Record<Key, string>>>({});
  const [busy, setBusy] = useState(false);
  const [day, setDay] = useState<{ date: string; text: string } | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const v: Settings = { ...saved, ...draft };
  const changed = (Object.keys(draft) as Key[]).filter((k) => !same(draft[k], saved[k]));
  const put = <K extends Key>(k: K, value: Settings[K]) => setDraft((d) => ({ ...d, [k]: value }));

  const errors: Partial<Record<Key, string>> = {};
  for (const [k, [lo, hi]] of Object.entries(NUM) as [Key, [number, number]][]) {
    const t = txt[k];
    if (t !== undefined && !(t.trim() !== "" && Number.isInteger(Number(t)) && Number(t) >= lo && Number(t) <= hi)) errors[k] = `Kies een geheel getal van ${lo} tot ${hi}.`;
  }
  if (!v.workdays.some(Boolean)) errors.workdays = "Kies minstens één werkdag.";
  if (!errors.full && !errors.over && !errors.room && !(v.room < v.full && v.full <= v.over)) errors.full = "Kleur vol moet boven Kleur ruimte liggen en niet boven Kleur overvol.";
  const nErr = Object.keys(errors).length;
  const errTabs = new Set(Object.keys(errors).map((k) => TAB_OF[k as Key]));
  const dirty = changed.length > 0 || nErr > 0;

  // niet zomaar weg met wijzigingen die nog niet opgeslagen zijn
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const reset = () => { setDraft({}); setTxt({}); setDay(null); setAddOpen(false); };
  const save = async () => {
    if (!changed.length || nErr) return;
    const values = Object.fromEntries(changed.map((k) => [k, v[k]])) as Partial<Settings>;
    const before = Object.fromEntries(changed.map((k) => [k, saved[k]])) as Partial<Settings>;
    setBusy(true);
    try {
      applySaved(await settingsApi.save(values));
      reset();
      const undo = async () => {
        try { applySaved(await settingsApi.save(before)); toast.flash("Ongedaan gemaakt"); } catch (e) { toast.flash((e as Error).message, { error: true }); }
      };
      toast.flash(plural(changed.length, "instelling", "instellingen") + " opgeslagen. Ze gelden binnen een minuut voor iedereen.", { undo });
    } catch (e) {
      toast.flash((e as Error).message, { error: true });
    } finally {
      setBusy(false);
    }
  };

  // ---------- velden ----------
  const num = (k: Key, label: string, help: string, unit: string) => {
    const [lo, hi] = NUM[k]!;
    return (
      <Row key={k} label={label} help={help} error={errors[k]}>
        <span className="p-num">
          <input className="ex-input" type="number" min={lo} max={hi} step={1} aria-label={label} aria-invalid={!!errors[k]}
            value={txt[k] ?? String(v[k])}
            onChange={(e) => {
              const t = e.target.value, n = Number(t);
              setTxt((x) => ({ ...x, [k]: t }));
              if (t.trim() !== "" && Number.isInteger(n) && n >= lo && n <= hi) setDraft((d) => ({ ...d, [k]: n }));
            }} />
          <span>{unit}</span>
        </span>
      </Row>
    );
  };
  const tog = (k: "ownTasks" | "pickUnowned" | "finishOthers" | "seeBoard" | "seeCapacity" | "excludeTemplate", label: string, help: string) => (
    <Row key={k} label={label} help={help}><Toggle label={label} on={v[k]} onClick={() => put(k, !v[k])} /></Row>
  );
  const notify = (k: NotifyKey, label: string, help: string) => (
    <Row key={k} label={label} help={help}><Toggle label={label} on={v.notify[k]} onClick={() => put("notify", { ...v.notify, [k]: !v.notify[k] })} /></Row>
  );
  const text = (label: string, help: ReactNode, value: ReactNode, tone = "") => (
    <Row key={label} label={label} help={help}><span className={"p-set-t" + (tone ? " " + tone : "")}>{value}</span></Row>
  );
  const name = (id: string) => idx.people.get(id)?.name || "Onbekende gebruiker";

  // labels uit alle klantplannen (zelfde naam = zelfde betekenis), plus wat al een betekenis heeft maar niet in gebruik is
  const labelRows = useMemo(() => {
    const rows = new Map<string, { key: string; name: string; n: number; used: boolean }>();
    for (const l of data?.labels || []) {
      const k = l.name.trim().toLowerCase();
      if (k && !rows.has(k)) rows.set(k, { key: k, name: l.name.trim(), n: 0, used: true });
    }
    for (const t of data?.tasks || []) for (const l of t.labels) { const r = rows.get(l.name.trim().toLowerCase()); if (r) r.n++; }
    for (const k of Object.keys(saved.labels)) if (!rows.has(k)) rows.set(k, { key: k, name: k.charAt(0).toUpperCase() + k.slice(1), n: 0, used: false });
    return [...rows.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [data, saved.labels]);

  const setLabel = (key: string, m: LabelMeaning) => {
    const next = { ...v.labels, [key]: m };
    if (m === "actief" && !(key in saved.labels)) delete next[key]; // een nieuw label is al actief werk
    put("labels", next);
  };

  const thisYear = todayIn().slice(0, 4);
  const addDay = () => {
    if (!day?.date) { toast.flash("Kies eerst een datum", { error: true }); return; }
    if (v.closingDays.some((x) => x.date === day.date)) { toast.flash("Die dag staat er al", { error: true }); return; }
    put("closingDays", [...v.closingDays, { date: day.date, label: day.text.trim().slice(0, 80) }].sort((a, b) => a.date.localeCompare(b.date)));
    setDay(null);
  };
  const candidates = (info?.planners || []).filter((id) => !v.superadmins.includes(id)).sort((a, b) => name(a).localeCompare(name(b)));

  const fields: Record<Tab, () => ReactNode> = {
    werktijd: () => [
      num("weekHours", "Standaard werkweek", "Geldt voor iedereen. De capaciteit per week is dit aantal uren min verlof en feestdagen.", "u per week"),
      <Row key="workdays" label="Werkdagen" help="Werk wordt alleen op deze dagen gespreid." error={errors.workdays}>
        <div className="p-quick">
          {DAYS.map((d, i) => (
            <button key={d} type="button" className={"p-tog p-tog-s" + (v.workdays[i] ? " on" : "")} aria-pressed={v.workdays[i]}
              onClick={() => put("workdays", v.workdays.map((x, j) => (j === i ? !x : x)))}>{d}</button>
          ))}
        </div>
      </Row>,
      <Row key="closingDays" block label="Sluitingsdagen" help="Dagen waarop niemand werkt, bovenop de feestdagen. Feestdagen komen uit Clockify.">
        {v.closingDays.length > 0 && (
          <div className="p-setrows">
            {v.closingDays.map((d) => (
              <div key={d.date} className="p-setr">
                <span className="p-setr-n">
                  <b>{ddmm(d.date)} {d.label || "Sluitingsdag"}</b>
                  <small>{dow(d.date) >= 5 ? "sluitingsdag in het weekend" : "sluitingsdag"}{d.date.slice(0, 4) !== thisYear ? " · " + d.date.slice(0, 4) : ""}</small>
                </span>
                <button type="button" className="ex-icon-btn ex-icon-btn--square p-setr-x" aria-label="Verwijderen" title="Verwijderen"
                  onClick={() => put("closingDays", v.closingDays.filter((x) => x.date !== d.date))}><Icon name="x" size={14} /></button>
              </div>
            ))}
          </div>
        )}
        {!day && <button type="button" className="p-tog p-set-add" onClick={() => setDay({ date: "", text: "" })}><Icon name="plus" size={13} />Sluitingsdag toevoegen</button>}
        {day && (
          <div className="p-setform">
            <input className="ex-input" type="date" aria-label="Datum" value={day.date} onChange={(e) => setDay({ ...day, date: e.target.value })} />
            <input className="ex-input" type="text" aria-label="Omschrijving" placeholder="bijvoorbeeld brugdag" maxLength={80} value={day.text}
              onChange={(e) => setDay({ ...day, text: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") addDay(); }} />
            <button type="button" className="ex-btn-xs ex-btn-xs--primary" onClick={addDay}>Toevoegen</button>
            <button type="button" className="ex-btn-xs" onClick={() => setDay(null)}>Annuleren</button>
          </div>
        )}
      </Row>,
    ],
    regels: () => [
      num("soonDays", "Binnenkort", "Een deadline binnen dit aantal dagen kleurt oranje.", "dagen"),
      num("lateDays", "Recent te laat", "Tot dit aantal dagen over de deadline kleurt een taak rood. Daarna heet ze verlopen en vraagt ze om herplannen.", "dagen"),
      num("horizonWeeks", "Capaciteit vooruit", "Aantal weken in het capaciteitsraster.", "weken"),
      num("full", "Kleur vol", "Vanaf dit percentage kleurt een week oranje.", "%"),
      num("over", "Kleur overvol", "Boven dit percentage kleurt een week rood.", "%"),
      num("room", "Kleur ruimte", "Onder dit percentage kleurt een week blauw.", "%"),
    ],
    labels: () => (
      <Row block label="Betekenis van elk label" help="Labels komen uit Planner Premium. De betekenis bepaalt waar een taak staat: bij het werk, in de wachtrij of klaar om af te sluiten. Een nieuw label start als actief werk; een label om te negeren telt niet mee en staat niet op de kaart.">
        <div className="p-setrows">
          {labelRows.map((r) => (
            <div key={r.key} className="p-setr">
              <i className="p-setr-dot" style={{ background: labelColor(r.name) }} />
              <span className="p-setr-n"><b>{r.name}</b><small>{r.used ? plural(r.n, "open taak", "open taken") : "in geen enkel klantplan"}</small></span>
              <select className="ex-input p-sel" aria-label={r.name} value={v.labels[r.key] || "actief"}
                onChange={(e) => setLabel(r.key, e.target.value as LabelMeaning)}>
                {CATS.map(([c, t]) => <option key={c} value={c}>{t}</option>)}
              </select>
            </div>
          ))}
        </div>
      </Row>
    ),
    rollen: () => [
      <Row key="superadmins" block label="Superadmin" help="Beheert deze instellingen. Kies uit de planners; uzelf verwijderen doet een andere superadmin.">
        <div className="p-setrows">
          {v.superadmins.map((id) => (
            <div key={id} className="p-setr">
              <span className="p-setr-n"><b>{name(id)}</b>{id === meId && <small>uzelf</small>}</span>
              {id !== meId && (
                <button type="button" className="ex-icon-btn ex-icon-btn--square p-setr-x" aria-label={"Verwijderen: " + name(id)} title="Verwijderen"
                  onClick={() => put("superadmins", v.superadmins.filter((x) => x !== id))}><Icon name="x" size={14} /></button>
              )}
            </div>
          ))}
        </div>
        {candidates.length > 0 && !addOpen && <button type="button" className="p-tog p-set-add" onClick={() => setAddOpen(true)}><Icon name="plus" size={13} />Superadmin toevoegen</button>}
        {addOpen && (
          <div className="p-set-addlist">
            <span>Kies een planner</span>
            <div className="p-quick">
              {candidates.map((id) => (
                <button key={id} type="button" className="p-tog p-tog-s" onClick={() => { put("superadmins", [...v.superadmins, id]); setAddOpen(false); }}>{name(id)}</button>
              ))}
              <button type="button" className="ex-btn-xs" onClick={() => setAddOpen(false)}>Annuleren</button>
            </div>
          </div>
        )}
      </Row>,
      text("Planners", <>Alles zien en alles wijzigen. Leden beheert u in Entra{info ? ", groep " + info.plannerGroup : ""}.</>,
        info?.planners.length ? info.planners.map(name).sort().join(", ") : "Leden van de plannersgroep"),
      text("Medewerkers", "Iedereen van TLCC die zich aanmeldt.", "Alle TLCC-gebruikers"),
      text("Teams en inzetbaarheid", "Teams inrichten en per persoon zetten hoeveel van een week inzetbaar is voor de planning.", "Alleen planners, in het scherm Teams"),
      <Row key="deadlineRight" label="Deadline wijzigen door een medewerker" help="Planners wijzigen altijd direct.">
        <Radios label="Deadline wijzigen door een medewerker" value={v.deadlineRight} onPick={(x) => put("deadlineRight", x)}
          opts={[["voorstel", "Als voorstel, de planner keurt goed"], ["direct", "Direct, zonder goedkeuring"], ["niet", "Niet toegestaan"]]} />
      </Row>,
      tog("ownTasks", "Eigen taken aanmaken", "Medewerkers maken taken aan voor zichzelf, in Planner."),
      tog("pickUnowned", "Taken zonder eigenaar oppakken", "Een medewerker wijst een taak zonder eigenaar toe aan zichzelf, nooit aan een collega."),
      tog("finishOthers", "Taken van collega's afwerken", "Uit: een medewerker werkt alleen eigen taken af."),
      tog("seeBoard", "Teambord bekijken", "Medewerkers zien het bord van het hele team."),
      tog("seeCapacity", "Capaciteit bekijken", "Medewerkers zien de bezetting van collega's."),
      <Row key="merged" label="Dubbele accounts" help="Gastaccounts of dubbele Planner-resources die als één persoon tellen.">
        <Link className="ex-link p-set-t" to="/koppelingen">Samenvoegen gebeurt in Koppelingen, tabblad Personen</Link>
      </Row>,
    ],
    bronnen: () => [
      text("Planner Premium", "Dataverse " + (info?.dataverse || "") + ". Klantplannen heten Planner met de naam van de klant.",
        data ? plural(data.klanten.length, "klantplan", "klantplannen") + " · verbonden" : "verbonden", "t-ok"),
      tog("excludeTemplate", "Sjabloonplan overslaan", "Template Premium Plan telt niet mee op het bord. Geldt vanaf de volgende verversing."),
      text("Planner verversen", "Wijzigingen in Planner verschijnen na hoogstens zoveel tijd. Een wijziging vanuit de app staat er meteen.", info ? every(info.refreshSeconds) : "Elke minuut"),
      text("Clockify", "Uren, schattingen, timers, verlof en feestdagen. Koppelen aan taken en klantplannen gebeurt in Koppelingen.", ...clockifyText(live.data?.clockify, !!live.error && !live.data)),
      text("Supportmailbox", "De gedeelde mailbox voor support: enkel onderwerp, afzender, datums en categorie, nooit de inhoud.", ...mailText(support.data?.sync, !!support.error && !support.data)),
      <Row key="snapshot" label="Dagelijkse momentopname" help="Bewaart elke dag de stand van alle taken, voor de trends.">
        <select className="ex-input p-sel" aria-label="Dagelijkse momentopname" value={v.snapshot} onChange={(e) => put("snapshot", e.target.value)}>
          {["05:00", "06:00", "07:00", "22:00"].map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
      </Row>,
    ],
    meldingen: () => [
      <div key="note" className="ex-notice ex-notice--mist p-notice" style={{ marginTop: 12 }}><Icon name="info" size={16} />
        <p>Het versturen van meldingen volgt nog; uw keuzes blijven bewaard. Ontbrekende uren en supportmails komen met de koppeling met Clockify en de supportmailbox.</p>
      </div>,
      notify("weekly", "Opschoonronde", "Vrijdag om 15:00 naar de planners, met het aantal voorstellen, verlopen deadlines en taken zonder eigenaar."),
      notify("dlToday", "Deadline vandaag", "Om 08:30 naar de eigenaar van de taak."),
      notify("proposal", "Nieuw datumvoorstel", "Meteen naar de planners, als een medewerker een nieuwe deadline voorstelt."),
      <Row key="channel" label="Kanaal">
        <Radios label="Kanaal" value={v.channel} onPick={(x) => put("channel", x)} opts={[["teams", "Teams-bericht"], ["mail", "E-mail"], ["beide", "Teams en e-mail"]]} />
      </Row>,
    ],
  };

  const last = audit.data?.[0];
  return (
    <>
      <div className="p-head"><div><h1>Instellingen</h1><p>Alleen voor de superadmin · wijzigingen gelden voor iedereen en worden bijgehouden</p></div></div>
      <div className="ex-settings-tabs p-set-tabs" role="tablist">
        {TABS.map(([k, label]) => (
          <button key={k} type="button" role="tab" className="ex-tab" aria-selected={tab === k} onClick={() => { setTab(k); setAddOpen(false); }}>
            {label}{errTabs.has(k) && <span className="ex-dot ex-dot--err" aria-label="bevat een fout" />}
          </button>
        ))}
      </div>
      <div className="p-setwrap">
        <section className="p-card p-set-main">
          <div className="p-set-body" role="tabpanel">{fields[tab]()}</div>
          <div className="p-set-bar">
            {dirty ? (
              <>
                <span className={nErr ? "t-err" : "t-warn"}>
                  {nErr ? plural(nErr, "waarde klopt", "waarden kloppen") + " nog niet" : plural(changed.length, "wijziging", "wijzigingen") + " nog niet opgeslagen"}
                </span>
                <span className="p-set-btns">
                  <button type="button" className="ex-btn" disabled={busy} onClick={reset}>Annuleren</button>
                  <button type="button" className="ex-btn ex-btn--primary" disabled={busy || nErr > 0 || !changed.length} onClick={save}><Icon name="check" size={15} />Opslaan</button>
                </span>
              </>
            ) : (
              <span className="t-mute">{last ? "Alles opgeslagen · " + when(last.at) + " door " + last.userName : "Alles opgeslagen · de standaardwaarden gelden"}</span>
            )}
          </div>
        </section>
        <div className="p-set-side">
          <section className="p-card">
            <div className="p-card-h"><div><h2>Laatste wijzigingen</h2><p>Elke wijziging wordt bewaard met naam en tijd</p></div></div>
            <div className="p-audit">
              {(audit.data || []).map((a) => (
                <div key={a.id} className="p-audit-i"><b>{a.details.text || a.action}</b><span>{when(a.at)} · {a.userName}</span></div>
              ))}
              {audit.data && !audit.data.length && <div className="p-audit-i"><span>Nog geen wijzigingen; de standaardwaarden gelden.</span></div>}
              {audit.error && <div className="p-audit-i"><span>Het logboek kon niet laden.</span></div>}
            </div>
          </section>
        </div>
      </div>
    </>
  );
}
